/**
 * Customers Firestore Service
 * Aggregates customer data from sales collection
 */

import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../config/firebase';

// Helper to convert Firestore timestamp to Date
const convertTimestamp = (timestamp) => {
  if (!timestamp) return null;
  if (timestamp.toDate) {
    return timestamp.toDate();
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }
  return new Date(timestamp);
};

/**
 * Get all customers aggregated from sales
 */
export const getAll = async (filters = {}) => {
  try {
    const salesRef = collection(db, 'sales');
    let constraints = [];

    if (filters.location) {
      constraints.push(where('location', '==', filters.location));
    }

    if (filters.startDate && filters.endDate) {
      constraints.push(where('date', '>=', Timestamp.fromDate(new Date(filters.startDate))));
      constraints.push(where('date', '<=', Timestamp.fromDate(new Date(filters.endDate))));
    } else if (filters.startDate) {
      constraints.push(where('date', '>=', Timestamp.fromDate(new Date(filters.startDate))));
    } else if (filters.endDate) {
      constraints.push(where('date', '<=', Timestamp.fromDate(new Date(filters.endDate))));
    }

    const q = constraints.length > 0 ? query(salesRef, ...constraints) : query(salesRef);
    const snapshot = await getDocs(q);

    // Aggregate customers by phone number
    const customerMap = new Map();

    snapshot.docs.forEach((doc) => {
      const sale = doc.data();
      const phone = sale.customerPhone?.trim();

      if (!phone) return; // Skip sales without phone numbers

      if (!customerMap.has(phone)) {
        customerMap.set(phone, {
          phone: phone,
          name: sale.customerName?.trim() || 'Unknown',
          location: sale.location?.trim() || '',
          totalPurchases: 0,
          totalSpent: 0,
          firstPurchaseDate: convertTimestamp(sale.date),
          lastPurchaseDate: convertTimestamp(sale.date),
          purchaseDates: [],
          products: new Map()
        });
      }

      const customer = customerMap.get(phone);
      const amount = parseFloat(sale.amount) || 0;
      const saleDate = convertTimestamp(sale.date);

      customer.totalPurchases += 1;
      customer.totalSpent += amount;
      customer.purchaseDates.push(saleDate);

      // Update first and last purchase dates
      if (saleDate < customer.firstPurchaseDate) {
        customer.firstPurchaseDate = saleDate;
      }
      if (saleDate > customer.lastPurchaseDate) {
        customer.lastPurchaseDate = saleDate;
      }

      // Track most recent name and location
      if (sale.customerName?.trim()) {
        customer.name = sale.customerName.trim();
      }
      if (sale.location?.trim()) {
        customer.location = sale.location.trim();
      }

      // Track products
      if (sale.productId) {
        const productId = sale.productId;
        if (!customer.products.has(productId)) {
          customer.products.set(productId, {
            productId: productId,
            productType: sale.productType || '',
            count: 0,
            totalSpent: 0
          });
        }
        const product = customer.products.get(productId);
        product.count += 1;
        product.totalSpent += amount;
      }
    });

    // Convert Map to Array and calculate additional metrics
    let customers = Array.from(customerMap.values()).map((customer) => {
      const averagePurchaseAmount =
        customer.totalPurchases > 0 ? customer.totalSpent / customer.totalPurchases : 0;

      const daysDiff =
        customer.lastPurchaseDate && customer.firstPurchaseDate
          ? Math.floor(
            (customer.lastPurchaseDate - customer.firstPurchaseDate) / (1000 * 60 * 60 * 24)
          )
          : 0;
      const purchaseFrequency =
        customer.totalPurchases > 1 && daysDiff > 0
          ? Math.round(daysDiff / (customer.totalPurchases - 1))
          : null;

      // Get favorite products (top 3 by count)
      const favoriteProducts = Array.from(customer.products.values())
        .sort((a, b) => b.count - a.count)
        .slice(0, 3)
        .map((p) => ({
          productId: p.productId,
          productType: p.productType,
          purchaseCount: p.count,
          totalSpent: p.totalSpent
        }));

      return {
        phone: customer.phone,
        name: customer.name,
        location: customer.location,
        totalPurchases: customer.totalPurchases,
        totalSpent: customer.totalSpent,
        averagePurchaseAmount: averagePurchaseAmount,
        firstPurchaseDate: customer.firstPurchaseDate,
        lastPurchaseDate: customer.lastPurchaseDate,
        purchaseFrequency: purchaseFrequency,
        favoriteProducts: favoriteProducts,
        purchaseFrequencyLabel: purchaseFrequency
          ? purchaseFrequency < 7
            ? 'Frequent'
            : purchaseFrequency < 30
              ? 'Regular'
              : purchaseFrequency < 90
                ? 'Occasional'
                : 'Rare'
          : 'New'
      };
    });

    // Apply sorting
    const sortBy = filters.sortBy || 'totalSpent';
    const sortOrder = filters.sortOrder || 'desc';

    customers.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      if (sortBy === 'lastPurchaseDate' || sortBy === 'firstPurchaseDate') {
        aVal = aVal ? aVal.getTime() : 0;
        bVal = bVal ? bVal.getTime() : 0;
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    // Apply pagination
    const limit = parseInt(filters.limit) || customers.length;
    const offset = parseInt(filters.offset) || 0;
    const paginatedCustomers = customers.slice(offset, offset + limit);

    return {
      customers: paginatedCustomers,
      total: customers.length,
      pagination: {
        limit,
        offset,
        total: customers.length
      }
    };
  } catch (error) {
    console.error('Error fetching customers:', error);
    throw error;
  }
};

/**
 * Get customer by phone number
 */
export const getById = async (phone) => {
  try {
    const salesRef = collection(db, 'sales');
    const q = query(salesRef, where('customerPhone', '==', phone.trim()), orderBy('date', 'desc'));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      throw new Error('Customer not found');
    }

    // Aggregate customer data
    let customer = {
      phone: phone.trim(),
      name: '',
      location: '',
      totalPurchases: 0,
      totalSpent: 0,
      firstPurchaseDate: null,
      lastPurchaseDate: null,
      sales: []
    };

    snapshot.docs.forEach((doc) => {
      const sale = doc.data();
      const amount = parseFloat(sale.amount) || 0;
      const saleDate = convertTimestamp(sale.date);

      if (!customer.name && sale.customerName?.trim()) {
        customer.name = sale.customerName.trim();
      }
      if (!customer.location && sale.location?.trim()) {
        customer.location = sale.location.trim();
      }

      customer.totalPurchases += 1;
      customer.totalSpent += amount;

      if (!customer.firstPurchaseDate || saleDate < customer.firstPurchaseDate) {
        customer.firstPurchaseDate = saleDate;
      }
      if (!customer.lastPurchaseDate || saleDate > customer.lastPurchaseDate) {
        customer.lastPurchaseDate = saleDate;
      }

      customer.sales.push({
        id: doc.id,
        ...sale,
        date: saleDate
      });
    });

    customer.averagePurchaseAmount =
      customer.totalPurchases > 0 ? customer.totalSpent / customer.totalPurchases : 0;

    return customer;
  } catch (error) {
    console.error('Error fetching customer:', error);
    throw error;
  }
};

/**
 * Get customer statistics
 */
export const getStats = async (phone) => {
  try {
    const customer = await getById(phone);

    const daysDiff =
      customer.lastPurchaseDate && customer.firstPurchaseDate
        ? Math.floor(
          (customer.lastPurchaseDate - customer.firstPurchaseDate) / (1000 * 60 * 60 * 24)
        )
        : 0;

    const purchaseFrequency =
      customer.totalPurchases > 1 && daysDiff > 0
        ? Math.round(daysDiff / (customer.totalPurchases - 1))
        : null;

    // Get product breakdown
    const products = new Map();
    customer.sales.forEach((sale) => {
      if (sale.productId) {
        if (!products.has(sale.productId)) {
          products.set(sale.productId, {
            productId: sale.productId,
            productType: sale.productType || '',
            count: 0,
            totalSpent: 0
          });
        }
        const product = products.get(sale.productId);
        product.count += 1;
        product.totalSpent += parseFloat(sale.amount) || 0;
      }
    });

    const favoriteProducts = Array.from(products.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return {
      ...customer,
      purchaseFrequency,
      purchaseFrequencyLabel: purchaseFrequency
        ? purchaseFrequency < 7
          ? 'Frequent'
          : purchaseFrequency < 30
            ? 'Regular'
            : purchaseFrequency < 90
              ? 'Occasional'
              : 'Rare'
        : 'New',
      favoriteProducts
    };
  } catch (error) {
    console.error('Error getting customer stats:', error);
    throw error;
  }
};

/**
 * Get customer purchase history
 */
export const getHistory = async (phone, options = {}) => {
  try {
    const salesRef = collection(db, 'sales');
    let constraints = [where('customerPhone', '==', phone.trim()), orderBy('date', 'desc')];

    if (options.limit) {
      constraints.push(firestoreLimit(parseInt(options.limit)));
    }

    const q = query(salesRef, ...constraints);
    const snapshot = await getDocs(q);

    const sales = snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        date: convertTimestamp(data.date),
        createdAt: convertTimestamp(data.createdAt),
        updatedAt: convertTimestamp(data.updatedAt)
      };
    });

    return sales;
  } catch (error) {
    console.error('Error getting customer history:', error);
    throw error;
  }
};

/**
 * Get overall customer statistics
 */
export const getOverview = async (filters = {}) => {
  try {
    const result = await getAll(filters);
    const customers = result.customers;

    const totalCustomers = customers.length;
    const totalRevenue = customers.reduce((sum, c) => sum + c.totalSpent, 0);
    const averageCustomerValue = totalCustomers > 0 ? totalRevenue / totalCustomers : 0;

    // Count new customers (first purchase this month)
    const now = new Date();
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const newCustomers = customers.filter((c) => {
      const firstPurchase = c.firstPurchaseDate;
      return firstPurchase && firstPurchase >= thisMonth;
    }).length;

    return {
      totalCustomers,
      totalRevenue,
      averageCustomerValue,
      newCustomersThisMonth: newCustomers
    };
  } catch (error) {
    console.error('Error getting customer overview:', error);
    throw error;
  }
};

// Export as object for compatibility with API pattern
const customersService = {
  getAll,
  getById,
  getStats,
  getHistory,
  getOverview
};

export default customersService;
